import { Attachment } from "../../domain/model/Attachment";
import { Auth, AccessToken, Message } from "../../domain/model/Auth";
import { Category } from "../../domain/model/Category";
import { Customer } from "../../domain/model/Customer";
import { Order, UpdateOrderModel } from "../../domain/model/Order";
import {
  DateRange,
  GetReportRequestModel,
  Product,
} from "../../domain/model/Product";
import { Store } from "../../domain/model/Store";
import { Transaction } from "../../domain/model/Transaction";
import { ApiService } from "../api/ApiService";
import { Response } from "../api/Response";
import { API_ENDPOINTS } from "../constant/ApiConstants";
import NetworkApi from "./NetworkApi";

export default class NetworkApiImpl implements NetworkApi {
  apiService: ApiService;

  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }
  login(auth: Auth): Promise<Response<AccessToken>> {
    return this.apiService.post<AccessToken>(API_ENDPOINTS.LOGIN, auth);
  }
  uploadFile(fileFormData: FormData): Promise<Response<Attachment>> {
    return this.apiService.post<Attachment>(
      API_ENDPOINTS.UPLOAD_FILE,
      fileFormData
    );
  }
  uploadMultipleFiles(
    filesFormData: FormData
  ): Promise<Response<Array<string>>> {
    return this.apiService.post<Array<string>>(
      API_ENDPOINTS.UPLOAD_FILE + API_ENDPOINTS.LIST,
      filesFormData
    );
  }

  // customers
  getCustomers(
    skip?: number,
    limit?: number
  ): Promise<Response<Array<Customer>>> {
    var url = `${API_ENDPOINTS.CUSTOMERS}`;
    if (limit !== undefined) {
      url += `?limit=${limit}`;
      if (skip !== undefined) {
        url += `&skip=${skip}`;
      }
    }
    return this.apiService.get<Array<Customer>>(url);
  }
  getCustomerById(id: string): Promise<Response<Customer>> {
    return this.apiService.get<Customer>(API_ENDPOINTS.CUSTOMERS + "/" + id);
  }
  searchCustomers(search: string): Promise<Response<Array<Customer>>> {
    return this.apiService.get<Array<Customer>>(
      `${API_ENDPOINTS.CUSTOMERS}${API_ENDPOINTS.SEARCH}?q=${search}`
    );
  }
  getCustomerOrders(
    customerId: string,
    skip?: number,
    limit?: number
  ): Promise<Response<Array<Order>>> {
    var url = `${API_ENDPOINTS.CUSTOMERS}${API_ENDPOINTS.ORDERS}?customerId=${customerId}`;
    if (limit !== undefined) {
      url += `&limit=${limit}`;
      if (skip !== undefined) {
        url += `&skip=${skip}`;
      }
    }
    return this.apiService.get<Array<Order>>(url);
  }

  // catgories
  createCategory(category: Category): Promise<Response<Message>> {
    return this.apiService.post<Message>(API_ENDPOINTS.CATEGORIES, category);
  }
  getCategories(): Promise<Response<Array<Category>>> {
    return this.apiService.get<Array<Category>>(API_ENDPOINTS.CATEGORIES);
  }
  getCategory(id: string): Promise<Response<Category>> {
    return this.apiService.get<Category>(`${API_ENDPOINTS.CATEGORIES}/${id}`);
  }
  updateCategory(category: Category): Promise<Response<Message>> {
    return this.apiService.put<Message>(
      `${API_ENDPOINTS.CATEGORIES}/${category._id}`,
      category
    );
  }
  deleteCategory(id: string): Promise<Response<Message>> {
    return this.apiService.delete<Message>(`${API_ENDPOINTS.CATEGORIES}/${id}`);
  }

  // store
  createStore(store: Store): Promise<Response<Message>> {
    return this.apiService.post<Message>(API_ENDPOINTS.STORES, store);
  }
  getStores(): Promise<Response<Array<Store>>> {
    return this.apiService.get<Array<Store>>(API_ENDPOINTS.STORES);
  }
  getStore(id: string): Promise<Response<Store>> {
    return this.apiService.get<Store>(`${API_ENDPOINTS.STORES}/${id}`);
  }
  updateStore(store: Store): Promise<Response<Message>> {
    return this.apiService.put<Message>(
      `${API_ENDPOINTS.STORES}/${store._id}`,
      store
    );
  }
  deleteStore(id: string): Promise<Response<Message>> {
    return this.apiService.delete<Message>(`${API_ENDPOINTS.STORES}/${id}`);
  }

  // product
  createProduct(product: Product): Promise<Response<Message>> {
    return this.apiService.post<Message>(API_ENDPOINTS.PRODUCTS, product);
  }
  getProducts(): Promise<Response<Array<Product>>> {
    return this.apiService.get<Array<Product>>(API_ENDPOINTS.PRODUCTS);
  }
  getProduct(id: string): Promise<Response<Product>> {
    return this.apiService.get<Product>(`${API_ENDPOINTS.PRODUCTS}/${id}`);
  }
  updateProduct(product: Product): Promise<Response<Message>> {
    return this.apiService.put<Message>(
      `${API_ENDPOINTS.PRODUCTS}/${product._id}`,
      product
    );
  }
  deleteProduct(id: string): Promise<Response<Message>> {
    return this.apiService.delete<Message>(`${API_ENDPOINTS.PRODUCTS}/${id}`);
  }
  getDeliveryPrice(): Promise<Response<number>> {
    return this.apiService.get<number>(
      API_ENDPOINTS.PRODUCTS + API_ENDPOINTS.DELIVERY_PRICE
    );
  }
  editDeliveryPrice(price: number): Promise<Response<Message>> {
    return this.apiService.post<Message>(
      API_ENDPOINTS.PRODUCTS + API_ENDPOINTS.DELIVERY_PRICE,
      { price }
    );
  }

  // orders
  getOrders(skip?: number, limit?: number): Promise<Response<Array<Order>>> {
    var url = `${API_ENDPOINTS.ORDERS}`;
    if (limit !== undefined) {
      url += `?limit=${limit}`;
      if (skip !== undefined) {
        url += `&skip=${skip}`;
      }
    }
    return this.apiService.get<Array<Order>>(url);
  }
  getOrderById(id: string): Promise<Response<Order>> {
    return this.apiService.get<Order>(API_ENDPOINTS.ORDERS + "/" + id);
  }
  updateOrder(
    id: string,
    updateModel: UpdateOrderModel
  ): Promise<Response<Message>> {
    return this.apiService.put<Message>(
      API_ENDPOINTS.ORDERS + "/" + id,
      updateModel
    );
  }

  // transactions
  getTransactions(
    skip?: number,
    limit?: number
  ): Promise<Response<Array<Transaction>>> {
    var url = `${API_ENDPOINTS.TRANSACTIONS}`;
    if (limit !== undefined) {
      url += `?limit=${limit}`;
      if (skip !== undefined) {
        url += `&skip=${skip}`;
      }
    }
    return this.apiService.get<Array<Transaction>>(url);
  }
  downloadOrderReceipt(id: string): Promise<Response<Blob>> {
    return this.apiService.get<Blob>(
      API_ENDPOINTS.ORDERS + "/" + id + API_ENDPOINTS.DOWNLOAD_RECEIPT,
      { responseType: "blob" }
    );
  }
  downloadMultipleOrderReceipts(ids: Array<string>): Promise<Response<Blob>> {
    return this.apiService.post<Blob>(
      API_ENDPOINTS.ORDERS + API_ENDPOINTS.DOWNLOAD_MULTIPLE_RECEIPTS,
      ids,
      { responseType: "blob" }
    );
  }

  // reports
  getPerProductReport(model: GetReportRequestModel): Promise<Response<Blob>> {
    var url = `${API_ENDPOINTS.REPORTS}${API_ENDPOINTS.PER_PRODUCT}`;
    if (model.dateRange !== undefined) {
      url += `?from=${model.dateRange.fromUTC}`;
      url += `&to=${model.dateRange.toUTC}`;
    }
    if (model.storeName !== undefined && model.storeId !== undefined) {
      url += `&storeId=${model.storeId}`;
      url += `&storeName=${model.storeName}`;
    }
    return this.apiService.get<Blob>(url, { responseType: "blob" });
  }
  getPerStoreReport(dateRange?: DateRange): Promise<Response<Blob>> {
    var url = `${API_ENDPOINTS.REPORTS}${API_ENDPOINTS.PER_STORE}`;
    if (dateRange !== undefined) {
      url += `?from=${dateRange.fromUTC}`;
      url += `&to=${dateRange.toUTC}`;
    }
    return this.apiService.get<Blob>(url, { responseType: "blob" });
  }
}
